import React, { useState } from 'react';
import { Key, Eye, EyeOff } from 'lucide-react';
import Button from '../../shared/Button';
import Input from '../../shared/Input';
import PasswordStrengthIndicator from '../../shared/PasswordStrengthIndicator';
import { useChangePassword } from '../../../services/vendorProfileService';
import { useToastContext } from '../../../contexts/ToastContext';
import { validatePasswordForm } from '../../../utils/passwordValidation';

const PasswordSecurityTab = () => {
  const toast = useToastContext();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [passwordErrors, setPasswordErrors] = useState({});

  const handlePasswordInputChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (passwordErrors[field]) {
      setPasswordErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleValidatePassword = () => {
    const validation = validatePasswordForm(passwordData);
    setPasswordErrors(validation.errors);
    return validation.isValid;
  };

  const changePasswordMutation = useChangePassword({
    onSuccess: () => {
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success('Password Updated', 'Your password has been changed successfully!');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update password';
      const errorDetails = error?.response?.data?.errors;
      
      // Handle validation errors
      if (errorDetails) {
        if (errorDetails.current_password) {
          setPasswordErrors(prev => ({ ...prev, currentPassword: errorDetails.current_password[0] }));
        }
        if (errorDetails.new_password) {
          setPasswordErrors(prev => ({ ...prev, newPassword: errorDetails.new_password[0] }));
        }
        toast.error('Validation Error', errorDetails.new_password?.[0] || errorDetails.current_password?.[0] || errorMessage);
      } else {
        setPasswordErrors(prev => ({ ...prev, newPassword: errorMessage }));
        toast.error('Password Update Failed', errorMessage);
      }
    }
  });

  const handleUpdatePassword = () => {
    if (!handleValidatePassword()) return;
    changePasswordMutation.mutate(passwordData);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">Change Password</h3>
        <p className="text-sm text-gray-600">Update your password to keep your account secure.</p>
      </div>

      <div className="space-y-4">
        <div className="relative">
          <Input
            label="Current Password"
            type={showCurrentPassword ? 'text' : 'password'}
            value={passwordData.currentPassword}
            onChange={(e) => handlePasswordInputChange('currentPassword', e.target.value)}
            error={passwordErrors.currentPassword}
            required
          />
          <button
            type="button"
            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
            className="absolute right-3 top-8 sm:top-9 text-gray-400 hover:text-gray-600"
          >
            {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>

        <div className="relative">
          <Input
            label="New Password"
            type={showNewPassword ? 'text' : 'password'}
            value={passwordData.newPassword}
            onChange={(e) => handlePasswordInputChange('newPassword', e.target.value)}
            error={passwordErrors.newPassword}
            required
          />
          <button
            type="button"
            onClick={() => setShowNewPassword(!showNewPassword)}
            className="absolute right-3 top-8 sm:top-9 text-gray-400 hover:text-gray-600"
          >
            {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>

        {/* Password Strength Indicator */}
        <PasswordStrengthIndicator password={passwordData.newPassword} />

        <div className="relative">
          <Input
            label="Confirm New Password"
            type={showConfirmPassword ? 'text' : 'password'}
            value={passwordData.confirmPassword}
            onChange={(e) => handlePasswordInputChange('confirmPassword', e.target.value)}
            error={passwordErrors.confirmPassword}
            required
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-8 sm:top-9 text-gray-400 hover:text-gray-600"
          >
            {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>

        <Button
          onClick={handleUpdatePassword}
          disabled={changePasswordMutation.isPending}
          className="flex items-center justify-center w-full sm:w-auto"
        >
          {changePasswordMutation.isPending ? (
            <>
              <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
              Updating Password...
            </>
          ) : (
            <>
              <Key className="w-4 h-4 mr-2" />
              Update Password
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default PasswordSecurityTab;

